/** Anzeigehilfen. Nichts hier darf werfen — eine kaputte Formatierung darf
 *  keine Seite mitreissen, sie soll im Zweifel den Rohwert zeigen. */

const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

/**
 * Cron-Ausdruck → deutscher Satz.
 *
 * Die alte Oberflaeche schrieb `0 *​/6 * * *` in Monospace an den Nutzer.
 * Wer das lesen kann, braucht die Oberflaeche nicht; wer sie braucht, kann
 * es nicht. Abgedeckt sind die Formen, die diese App tatsaechlich erzeugt;
 * alles Exotische faellt auf den Rohausdruck zurueck, statt etwas Falsches
 * zu behaupten.
 */
export function cronText(expr: string): string {
  const teile = (expr || '').trim().split(/\s+/);
  if (teile.length !== 5) return expr;
  const [min, std, tag, monat, wtag] = teile;

  const jederTag = tag === '*' && monat === '*' && wtag === '*';
  const m = Number(min);
  const h = Number(std);

  // alle N Stunden
  const stdIntervall = /^\*\/(\d+)$/.exec(std);
  if (stdIntervall && jederTag && /^\d+$/.test(min)) {
    const n = Number(stdIntervall[1]);
    const zusatz = m === 0 ? '' : ` (jeweils zur ${pad(m)}. Minute)`;
    return n === 1 ? `stündlich${zusatz}` : `alle ${n} Stunden${zusatz}`;
  }

  // alle N Minuten
  const minIntervall = /^\*\/(\d+)$/.exec(min);
  if (minIntervall && std === '*' && jederTag) {
    const n = Number(minIntervall[1]);
    return n === 1 ? 'jede Minute' : `alle ${n} Minuten`;
  }

  // täglich zu fester Uhrzeit
  if (/^\d+$/.test(min) && /^\d+$/.test(std) && jederTag) {
    return `täglich um ${pad(h)}:${pad(m)} Uhr`;
  }

  // wöchentlich
  if (/^\d+$/.test(min) && /^\d+$/.test(std) && tag === '*' && monat === '*' && /^\d$/.test(wtag)) {
    return `jeden ${WOCHENTAGE[Number(wtag)] ?? wtag} um ${pad(h)}:${pad(m)} Uhr`;
  }

  // stündlich zur Minute N
  if (/^\d+$/.test(min) && std === '*' && jederTag) {
    return `stündlich zur ${pad(m)}. Minute`;
  }

  return expr;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Nächste Ausführung als grobe Angabe. Nur fuer die Formen, die `cronText`
 *  sicher versteht — sonst lieber nichts sagen als etwas Falsches. */
export function naechsterLauf(expr: string, ab: Date = new Date()): Date | null {
  const teile = (expr || '').trim().split(/\s+/);
  if (teile.length !== 5) return null;
  const [min, std, tag, monat, wtag] = teile;
  if (tag !== '*' || monat !== '*' || wtag !== '*') return null;
  const m = Number(min);
  if (!/^\d+$/.test(min)) return null;

  const stdIntervall = /^\*\/(\d+)$/.exec(std);
  if (stdIntervall) {
    const n = Number(stdIntervall[1]);
    if (!n) return null;
    const d = new Date(ab);
    d.setMinutes(m, 0, 0);
    while (d <= ab || d.getHours() % n !== 0) d.setHours(d.getHours() + 1);
    return d;
  }
  if (/^\d+$/.test(std)) {
    const d = new Date(ab);
    d.setHours(Number(std), m, 0, 0);
    if (d <= ab) d.setDate(d.getDate() + 1);
    return d;
  }
  if (std === '*') {
    const d = new Date(ab);
    d.setMinutes(m, 0, 0);
    if (d <= ab) d.setHours(d.getHours() + 1);
    return d;
  }
  return null;
}

export function bytes(n: number | null | undefined): string {
  if (!n || n < 0) return '—';
  const e = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < e.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${e[i]}`;
}

/** "vor 4 Min." / "in 5 Std." — Vorzeichen inklusive. */
export function relativ(iso: string | null | undefined, jetzt: Date = new Date()): string {
  if (!iso) return 'nie';
  const t = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
  if (Number.isNaN(t.getTime())) return '—';
  const s = Math.round((t.getTime() - jetzt.getTime()) / 1000);
  const kuenftig = s > 0;
  const a = Math.abs(s);
  const [wert, einheit] =
    a < 60 ? [a, 'Sek.']
    : a < 3600 ? [Math.round(a / 60), 'Min.']
    : a < 86400 ? [Math.round(a / 3600), 'Std.']
    : [Math.round(a / 86400), 'Tg.'];
  return kuenftig ? `in ${wert} ${einheit}` : `vor ${wert} ${einheit}`;
}

export function uhrzeit(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
  if (Number.isNaN(t.getTime())) return '—';
  return t.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Das `details`-Feld eines Laufs ist JSON-Text vom Executor. Genau hier
 * steht, was ein Lauf wirklich getan hat — die alte Oberflaeche hat es nie
 * angezeigt, obwohl es die interessanteste Information der ganzen Seite ist.
 */
const MELDUNGEN: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  [/^No new episodes found after E(\d+)$/, (m) => `keine neue Folge nach E${m[1]}`],
  [/^No results$/i, () => 'keine Treffer'],
  [/^Nothing new$/i, () => 'nichts Neues'],
];

function uebersetzeMeldung(text: string): string {
  for (const [re, bau] of MELDUNGEN) {
    const m = text.match(re);
    if (m) return bau(m);
  }
  return text;
}

export function laufText(log: { status: string; downloads_pushed: number; details: string | null; error_message: string | null }): string {
  if (log.status === 'error') return log.error_message?.slice(0, 160) || 'Fehler';
  let d: Record<string, unknown> = {};
  try {
    d = log.details ? JSON.parse(log.details) : {};
  } catch {
    /* kaputtes JSON ist kein Grund, die Zeile zu verlieren */
  }
  const stuecke: string[] = [];
  if (log.downloads_pushed > 0) {
    const eps = d.downloaded_episodes;
    stuecke.push(
      Array.isArray(eps) && eps.length
        ? `${log.downloads_pushed} geladen (E${eps.slice(0, 6).join(', E')}${eps.length > 6 ? ' …' : ''})`
        : `${log.downloads_pushed} geladen`,
    );
  }
  if (typeof d.message === 'string') stuecke.push(uebersetzeMeldung(d.message));
  if (typeof d.last_downloaded_episode === 'number' && d.last_downloaded_episode > 0) {
    stuecke.push(`Stand E${d.last_downloaded_episode}`);
  }
  if (typeof d.rate_limited === 'string') stuecke.push(d.rate_limited);
  if (typeof d.title_filtered === 'number' && d.title_filtered) {
    stuecke.push(`${d.title_filtered} Titel passten nicht`);
  }
  if (typeof d.wrong_season_filtered === 'number' && d.wrong_season_filtered) {
    stuecke.push(`${d.wrong_season_filtered} aus anderer Staffel`);
  }
  return stuecke.join(' · ') || (log.status === 'no_results' ? 'nichts Neues gefunden' : log.status);
}
